import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import BreadCrumbs from "../components/Breadcrumbs";
import {useParams} from "react-router-dom";
import {observer} from "mobx-react-lite";
import Container from "@mui/material/Container";
import {Context} from "..";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import {SERVERS_ROUTE} from "../utils/consts";
import {fetchServerLogsAPI, sendRcon} from "../http/ServerAPI";
import {getApiErrorMessage} from "../http/apiError";
import {useSnackbar} from "../components/snackbar/SnackbarProvider";

const normalizeLogResponse = (response, serverId) => {
    const payload = response?.data || response;
    const logs = payload?.logs || payload?.data || [];

    return {
        log: Number(payload?.server_id || serverId),
        data: Array.isArray(logs) ? logs : [],
    };
};

const Log = observer(() => {
    const {server_id} = useParams();
    const numericServerId = useMemo(() => Number(server_id), [server_id]);

    const {user} = useContext(Context);
    const snackbar = useSnackbar();

    const [name, setName] = useState('');
    const [role, setRole] = useState('');
    const [isLoadingLogs, setIsLoadingLogs] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [message, setMessage] = useState("");

    const hrefs = [
        { href: SERVERS_ROUTE, name: "Список серверов" },
    ];

    const chatEndRef = useRef(null);

    useEffect(() => {
        if (user.userName) {
            setName(user.userName);
            setRole(user.role);
        } else {
            setName('');
            setRole('');
        }
    }, [user.userName, user.role]);

    useEffect(() => {
        let isMounted = true;

        const loadInitialLogs = async () => {
            user.clearLog(numericServerId);
            setChatMessages([]);
            setIsLoadingLogs(true);

            try {
                const response = await fetchServerLogsAPI(numericServerId);
                const storage = normalizeLogResponse(response, numericServerId);

                if (!isMounted) {
                    return;
                }

                user.replaceLog(storage);
                setChatMessages(storage.data);
            } catch (e) {
                if (!isMounted) {
                    return;
                }

                snackbar.error(getApiErrorMessage(e, 'Не удалось загрузить историю логов'));
            } finally {
                if (isMounted) {
                    setIsLoadingLogs(false);
                }
            }
        };

        if (numericServerId) {
            loadInitialLogs();
        }

        return () => {
            isMounted = false;
        };
    }, [numericServerId, snackbar, user]);

    useEffect(() => {
        const logData = user.getStorageByLogId(numericServerId);
        setChatMessages(logData.data);
    }, [user.log, numericServerId, user]);

    useEffect(() => {
        const scrollToBottom = () => {
            if (chatEndRef.current) {
                chatEndRef.current.scrollIntoView({ behavior: "smooth" });
            }
        };

        scrollToBottom();
        const timeoutId = setTimeout(scrollToBottom, 100);

        return () => clearTimeout(timeoutId);
    }, [chatMessages]);

    const sendMessage = async (serverId, command) => {
        try {
            await sendRcon(serverId, command, 'command');
            snackbar.success('Команда отправлена');
        } catch (e) {
            snackbar.error(getApiErrorMessage(e, 'Не удалось отправить команду'));
        }
    };

    const handleSendMessage = () => {
        const command = message.trim();

        if (command) {
            sendMessage(numericServerId, command);
            setMessage("");
        }
    };

    return (
        <Container maxWidth="xl" sx={{display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 70px)', padding: '0'}}>
            <Box sx={{ width: '100%', marginTop: 3 }}>
                <BreadCrumbs hrefs={hrefs} name='Логи сервера' />
            </Box>
            <h1>Логи сервера {server_id}</h1>

            <Box
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    border: '1px solid #4f4f4f',
                    padding: '10px',
                    marginBottom: '10px',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#4f4f4f',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: '#555',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#1a1a1a',
                    }
                }}
            >
                {isLoadingLogs ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={22} />
                        <Typography>Загружаем последние записи лога...</Typography>
                    </Box>
                ) : chatMessages && chatMessages.length > 0 ? (
                    <ul style={{ listStyleType: 'none', padding: 0 }}>
                        {chatMessages.map((item, index) => (
                            <li
                                key={`${item.date || 'log'}-${index}`}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    marginBottom: '10px',
                                }}
                            >
                                <strong style={{ marginRight: '10px' }}>{new Date(item.date).toLocaleString()}:</strong>
                                <span>{item.message}</span>
                            </li>
                        ))}
                        <div ref={chatEndRef} />
                    </ul>
                ) : (
                    <p>Сообщений пока нет</p>
                )}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '70px',
                    padding: '10px',
                    borderTop: '1px solid #4f4f4f',
                    backgroundColor: 'rgb(46, 51, 52)'
                }}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Введите команду"
                    sx={{ marginRight: '10px' }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && message.trim()) {
                            handleSendMessage();
                        }
                    }}
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={!message.trim()}
                >
                    Отправить
                </Button>
            </Box>
        </Container>
    );
});

export default Log;
